<template>
  <div class="container">
    <ViewHeader :title="$t('message.storeLocator.faqTitle')">
      <ui-button
        class="ml-4 mr-auto"
        outlined
        icon="mdi-chevron-left"
        @click="goToStoreLocator"
      >
        {{ $t("message.storeLocator.backToStoreLocator") }}
      </ui-button>
    </ViewHeader>
    <v-card flat class="section-container">
      <PageHeader :title="$t('message.storeLocator.faqSubtitle')" />
      <div class="section-container__content">
        <StoreLocatorFaqSection
          :title="$t('message.storeLocator.faq.toAllLocations')"
          :faq-items="commonFaq"
          readonly
          @manage="goToEdit"
        >
          <template #default="{ items }">
            <StoreLocatorFaqListItem
              v-for="item in items"
              :key="item.id"
              :question="item.question"
              :answer="item.answer"
              readonly
            />
          </template>
        </StoreLocatorFaqSection>

        <StoreLocatorFaqSection
          :title="$t('message.storeLocator.faq.toLocation')"
          :faq-items="locationFaq"
          :facility-data="facilityData"
          :expandable="false"
          @manage="showAddDialog = true"
        >
          <template #default="{ items }">
            <StoreLocatorFaqListItem
              v-for="(item, index) in items"
              :key="item.id"
              :question="item.question"
              :answer="item.answer"
              :date="item.created_at"
              :is-last="index === locationFaq.length - 1"
              :is-first="index === 0"
              @move="handleMove(item.id, $event)"
              @update="handleUpdate(item.id, $event)"
              @delete="handleDelete(item.id)"
            />
          </template>
        </StoreLocatorFaqSection>
      </div>
      <SectionLoader :status="isLoading" :opacity="1" />
    </v-card>
    <StoreLocatorFaqManageDialog
      v-if="showAddDialog"
      @close="showAddDialog = false"
      @create="handleCreate"
    />
  </div>
</template>

<script>
import SectionLoader from "@/components/Loader/SectionLoader.vue";
import serviceHandler from "@/helpers/handlers/service";
import storeLocatorService from "@/services/storeLocator";
import ViewHeader from "@/components/ui/ViewHeader.vue";
import PageHeader from "@/components/BusinessAddresses/StoreLocatorPageHeader.vue";
import StoreLocatorFaqListItem from "@/components/StoreLocatorFaq/StoreLocatorFaqListItem.vue";
import StoreLocatorFaqSection from "@/components/BusinessAddresses/StoreLocatorFaqSection.vue";
import facilitiesService from "@/services/facilities";
import { storeLocatorFaqMixin } from "@/helpers/mixins/store-locator-faq";
export default {
  name: "StoreLocatorFaqTab",
  mixins: [storeLocatorFaqMixin],
  components: {
    SectionLoader,
    ViewHeader,
    PageHeader,
    StoreLocatorFaqListItem,
    StoreLocatorFaqSection,
    StoreLocatorFaqManageDialog: () =>
      import("@/components/StoreLocatorFaq/StoreLocatorFaqManageDialog.vue")
  },
  data() {
    return {
      isLoading: false,
      locationFaq: [],
      facilityData: null,
      facilityId: this.$route.params.id,
      showAddDialog: false
    };
  },

  async created() {
    await this.initializeFaq();
  },

  methods: {
    goToEdit() {
      this.$router.push({
        name: "store-locator-faq"
      });
    },
    goToStoreLocator() {
      this.$router.push({
        name: "store-locator-config",
        params: { id: this.facilityId }
      });
    },
    async handleMove(id, payload) {
      await this.withLoadingState(async () => {
        await serviceHandler(
          storeLocatorService.patchFacilityFaq(this.facilityId, { id, payload })
        );
        await this.getFacilityFaq();
      });
    },
    async handleUpdate(id, payload) {
      await this.withLoadingState(async () => {
        await serviceHandler(
          storeLocatorService.patchFacilityFaq(this.facilityId, { id, payload })
        );
        await this.getFacilityFaq();
      });
    },
    async handleDelete(id) {
      await this.withLoadingState(async () => {
        await serviceHandler(
          storeLocatorService.deleteFacilityFaq(this.facilityId, id)
        );
        await this.getFacilityFaq();
      });
    },
    async handleCreate(data) {
      this.showAddDialog = false;
      await this.withLoadingState(async () => {
        await serviceHandler(
          storeLocatorService.postFacilityFaq(this.facilityId, data)
        );
        await this.getFacilityFaq();
      });
    },
    async initializeFaq() {
      await this.withLoadingState(async () => {
        await Promise.all([
          this.fetchFacilityData(),
          this.getFacilityFaq(),
          this.getCommonFaq()
        ]);
      });
    },
    async getFacilityFaq() {
      this.locationFaq = await serviceHandler(
        storeLocatorService.getFacilityFaq(this.facilityId)
      );
    },
    async fetchFacilityData() {
      this.facilityData = await serviceHandler(
        facilitiesService.getFacilityData(this.$route.params.id)
      );
    },
    async postFacilityFaq(payload) {
      await this.withLoadingState(async () => {
        await serviceHandler(
          storeLocatorService.getFacilityFaq(this.facilityId, payload)
        );
        this.getFacilityFaq();
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.section-container {
  padding: $spacing * 2 $spacing * 4;

  &__content {
    display: flex;
    flex-direction: column;
    gap: $spacing * 2;
  }
}
</style>
